import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button, Dialog, Icon, Segmented } from '@etnas/design-system';
import { authApi, configureClient, isApiError, session, type StepUpPrompt } from '@/api';
import { Modal } from '@/components/Modal';
import { useAuth } from './auth';

interface Pending {
  prompt: StepUpPrompt;
  resolve: (token: string | null) => void;
}

const Ctx = createContext<{ ensureStepUp: (label: string) => Promise<boolean> } | null>(null);

/**
 * Reusable identity check. Registered as the API client's step-up handler: any command that returns
 * 401 ETNAS_STEP_UP_REQUIRED opens this dialog and, on success, the client retries the same request with the
 * same Idempotency-Key. Cancelling leaves the record unchanged.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const ask = useCallback((prompt: StepUpPrompt) => {
    // Serialize: never stack two identity prompts.
    const p = queue.current.then(
      () =>
        new Promise<string | null>((resolve) => {
          const existing = session.validStepUp();
          if (existing) return resolve(existing);
          setPending({ prompt, resolve });
        }),
    );
    queue.current = p.catch(() => null);
    return p;
  }, []);

  useEffect(() => {
    configureClient({ stepUpHandler: ask });
    return () => configureClient({ stepUpHandler: undefined });
  }, [ask]);

  const ensureStepUp = useCallback(async (label: string) => !!(await ask({ label, method: 'POST', path: '' })), [ask]);

  return (
    <Ctx.Provider value={{ ensureStepUp }}>
      {children}
      {pending ? (
        <StepUpDialog
          prompt={pending.prompt}
          onDone={(token) => {
            pending.resolve(token);
            setPending(null);
          }}
        />
      ) : null}
    </Ctx.Provider>
  );
}

export function useStepUp() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useStepUp outside StepUpProvider');
  return c;
}

function StepUpDialog({ prompt, onDone }: { prompt: StepUpPrompt; onDone: (token: string | null) => void }) {
  const { me } = useAuth();
  const [method, setMethod] = useState<'password' | 'code'>('code');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!value.trim()) {
      setError(method === 'code' ? 'Enter the 6-digit code from your authenticator.' : 'Enter your password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await authApi.stepUp(method === 'code' ? { code: value.trim() } : { password: value });
      onDone(r.stepUpToken);
    } catch (err) {
      setError(isApiError(err) ? err.detail ?? err.title : 'Could not verify. Try again.');
      setBusy(false);
    }
  };

  return (
    <Modal onClose={() => onDone(null)} top initialFocus="#stepup-value">
      <Dialog
        title="Confirm it is you"
        eyebrow="Identity check"
        width={460}
        onClose={() => onDone(null)}
        footer={
          <>
            <Button variant="quiet" onClick={() => onDone(null)} disabled={busy}>
              Cancel — change nothing
            </Button>
            <Button variant="primary" icon="key" loading={busy} onClick={() => submit()}>
              Verify and continue
            </Button>
          </>
        }
      >
        <div className="ex-box">
          <div className="ex-lbl">You are about to</div>
          <div className="ex-strong">{prompt.label}</div>
          <div className="ex-small ex-fg-muted">
            Signed in as {me?.user.displayName ?? 'you'} · the check lasts 5 minutes for further high-risk actions.
          </div>
        </div>
        <form onSubmit={submit} className="ex-stack is-tight">
          <Segmented
            label="Verification method"
            value={method}
            onChange={(v) => {
              setMethod(v as 'password' | 'code');
              setValue('');
              setError(null);
            }}
            items={[
              { id: 'code', label: 'Authenticator code', icon: 'device' },
              { id: 'password', label: 'Password', icon: 'key' },
            ]}
          />
          <div className={'ex-field' + (error ? ' is-error' : '')}>
            <label className="ex-field-label" htmlFor="stepup-value">
              {method === 'code' ? '6-digit code' : 'Password'}
            </label>
            <div className="ex-field-box">
              <input
                id="stepup-value"
                className={'ex-input' + (method === 'code' ? ' ex-mono' : '')}
                type={method === 'code' ? 'text' : 'password'}
                inputMode={method === 'code' ? 'numeric' : undefined}
                autoComplete={method === 'code' ? 'one-time-code' : 'current-password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-invalid={!!error || undefined}
                aria-describedby={error ? 'stepup-err' : 'stepup-hint'}
              />
            </div>
            {error ? (
              <div id="stepup-err" className="ex-field-note ex-fg-critical" role="alert">
                <Icon name="critical" size={12} knockout="var(--surface)" />
                {error}
              </div>
            ) : (
              <div id="stepup-hint" className="ex-field-note ex-fg-muted">
                {method === 'code' ? 'Development builds also accept 000000.' : 'Your ETNAS sign-in password.'}
              </div>
            )}
          </div>
          <button type="submit" hidden />
        </form>
      </Dialog>
    </Modal>
  );
}
